import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../../identity/types';
import { peopleGateway } from '../index';
import {
  ContractorIdentificationType,
  contractorIdentificationTypeLabels,
  contractorPayMethodLabels,
  type ContractorResponse,
} from '../types';

/**
 * EPN-74: a real contractor profile/edit screen — the Contractor List previously had no route-out at all
 * (only a status-toggle action). Shows and edits exactly the fields the real backend supports today
 * (`ContractorResponse`/`UpdateContractorRequest`); see docs/epn-74-contractor-management-gaps.md for what
 * the approved design shows that has no backend truth yet (address, discipline, documents/W-9, bank
 * account details, the richer Active/Pending/Inactive status).
 */
export function ContractorProfilePage() {
  const { contractorId } = useParams<{ contractorId: string }>();
  const navigate = useNavigate();
  const [contractor, setContractor] = useState<ContractorResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  const [displayName, setDisplayName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [identificationType, setIdentificationType] = useState<number>(ContractorIdentificationType.Ssn);
  const [identificationLast4, setIdentificationLast4] = useState('');
  const [preferredPayMethod, setPreferredPayMethod] = useState<string>('');
  const [isActive, setIsActive] = useState(true);
  // EPN-250: W-9 / 1099 data Pay Contractors' readiness reads. COMPLIANCE RULE CONFIRMATION REQUIRED: what counts as a completed W-9.
  const [w9ReceivedOn, setW9ReceivedOn] = useState('');
  const [form1099Configured, setForm1099Configured] = useState(false);

  const load = useCallback(async () => {
    if (!contractorId) return;
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      // Always reloaded from the backend by id — never trusted from the list page's own state (same
      // reasoning as EmployeeProfilePage's own loadEmployee comment). The backend's own company scoping
      // (ContractorService.GetByIdAsync -> FindInOwnCompanyAsync) is what actually rejects a manipulated
      // cross-company contractorId; a 404 here is a safe presentation of that rejection, not a second
      // access control.
      const found = await peopleGateway.getContractor(contractorId);
      setContractor(found);
      setDisplayName(found.displayName);
      setBusinessName(found.businessName ?? '');
      setEmail(found.email);
      setIdentificationType(found.identificationType);
      setIdentificationLast4(found.identificationLast4 ?? '');
      setPreferredPayMethod(found.preferredPayMethod === null ? '' : String(found.preferredPayMethod));
      setIsActive(found.isActive);
      setW9ReceivedOn(found.w9ReceivedOn ?? '');
      setForm1099Configured(found.form1099Configured);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setNotFound(true);
      } else {
        setError(err instanceof Error ? err.message : 'Could not load this contractor.');
      }
    } finally {
      setLoading(false);
    }
  }, [contractorId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  async function handleSave() {
    if (!contractorId) return;
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const updated = await peopleGateway.updateContractor(contractorId, {
        displayName,
        businessName: businessName.trim() === '' ? null : businessName,
        email,
        identificationType,
        identificationLast4: identificationLast4.trim() === '' ? null : identificationLast4,
        preferredPayMethod: preferredPayMethod === '' ? null : Number(preferredPayMethod),
        // Both are always sent together: the server leaves both unchanged when the 1099 flag is omitted.
        w9ReceivedOn: w9ReceivedOn === '' ? null : w9ReceivedOn,
        form1099Configured,
        isActive,
      });
      setContractor(updated);
      setSavedMessage('Saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save this contractor.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <section className="card card--wide">
        <p className="muted">Loading…</p>
      </section>
    );
  }

  if (notFound || !contractor) {
    return (
      <section className="card card--wide">
        <p className="error">Contractor not found.</p>
        <button type="button" className="link" onClick={() => navigate('/people/contractors')}>
          Back to Contractor List
        </button>
      </section>
    );
  }

  return (
    <section className="card card--wide">
      <div className="section-header">
        <h2>{contractor.displayName}</h2>
        <button type="button" className="link" onClick={() => navigate('/people/contractors')}>
          Back to Contractor List
        </button>
      </div>

      {error && <p className="error">{error}</p>}
      {savedMessage && <p className="success">{savedMessage}</p>}

      <div className="stat-grid">
        <label>
          Display Name
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
        </label>
        <label>
          Business Name
          <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="(individual)" />
        </label>
        <label>
          Email Address
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        <label>
          Identification Type
          <select value={identificationType} onChange={(e) => setIdentificationType(Number(e.target.value))}>
            {Object.entries(contractorIdentificationTypeLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
        <label>
          Identification Number (last 4)
          <input
            value={identificationLast4}
            onChange={(e) => setIdentificationLast4(e.target.value)}
            maxLength={4}
            placeholder="1234"
          />
        </label>
        <label>
          Preferred Pay Method
          <select value={preferredPayMethod} onChange={(e) => setPreferredPayMethod(e.target.value)}>
            <option value="">Not specified</option>
            {Object.entries(contractorPayMethodLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
        <label>
          W-9 received on
          <input type="date" value={w9ReceivedOn} onChange={(e) => setW9ReceivedOn(e.target.value)} />
        </label>
        <label>
          1099 reporting
          <span>
            <input type="checkbox" checked={form1099Configured} onChange={(e) => setForm1099Configured(e.target.checked)} /> Set up for this contractor
          </span>
        </label>
        <label>
          Status
          <select value={isActive ? 'active' : 'inactive'} onChange={(e) => setIsActive(e.target.value === 'active')}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>
      </div>

      <p className="muted">Added {new Date(contractor.createdAtUtc).toLocaleDateString()}</p>

      <div className="section-header">
        <button type="button" disabled={saving} onClick={() => void handleSave()}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </section>
  );
}
